import { zoneLabel, type SiteLocalization } from '@/localization';
import { el, setClass } from './dom';
import { icon } from './Icons';
import { LocalizeSection, type LocalizeSectionDeps } from './LocalizeSection';

/**
 * **Where this site is** — a panel above the flora palette.
 *
 * ## Why here rather than in the environment tray
 *
 * It started in the tray, filed under Site alongside latitude, which is where it *belongs* by
 * category and the wrong place for it entirely by use. Localizing is one of the first things anyone
 * does with a new site and it changes what every tile in the palette says; burying it three sections
 * down a scrolling tray on the opposite side of the window meant the one action that lights up the
 * whole left-hand column was the hardest thing on screen to find.
 *
 * So it sits directly above the thing it affects. The counts, the badges and the "best for this site"
 * sort all appear in the panel immediately below this one, which makes the cause and the effect a
 * single glance apart.
 *
 * ## The same panel as every other panel
 *
 * `lg-panel` with an `lg-panel__head` and an `lg-panel__body`, exactly like Flora and Environment:
 * the pin in the icon slot, the place in the title slot, the zone in the count slot where Flora puts
 * `54/54`, and a chevron that shuts it. Nothing bespoke. A first attempt built a custom bar with its
 * own typography and padding and it read as something bolted on from another application, which — for
 * a panel sitting flush against the palette — is exactly the wrong impression.
 *
 * The title is the *place* rather than a fixed word because that is the useful thing to see when the
 * panel is shut, and because "Site" is already the name of the inspector in the opposite tray.
 *
 * ## Two collapse states, deliberately distinct
 *
 * - `is-shut` hides the body and keeps the head. This is the panel's own chevron: vertical space in
 *   that column belongs to the plants, and once localized the head alone answers "where am I".
 * - `is-collapsed` is the palette's rail collapse, followed so the column stays one width.
 *
 * Unlocalized it opens itself, because a head reading "Localize site" over a hidden body would be a
 * label pretending to be a control.
 *
 * ## And it shuts itself once the job is done
 *
 * A successful localize arms {@link SITE_SETTLE_MS}: the summary stays up long enough to be read and
 * checked — this is the moment someone confirms the app understood which ZIP they meant — and then
 * the panel folds to its head and gives the column back to the plants, which is where the user is
 * going next anyway.
 *
 * Any click or keypress inside the panel restarts that clock, so reading the summary, ticking
 * *Sheltered*, or retyping the ZIP is never cut off mid-thought; working the chevron cancels it for
 * good, because a panel that shuts itself after being deliberately opened is a panel arguing with
 * its user.
 */

export interface SitePanelDeps extends LocalizeSectionDeps {
  /** Follows the flora palette: a 44px rail has no room for a place name. */
  collapsed?: () => boolean;
}

/**
 * How long the panel stays open after it localizes, and how much time each click inside it buys
 * back. Milliseconds.
 *
 * Three seconds is a glance at four short lines, not a read of them — which is the right length,
 * because it is only checking that the place name matches the ZIP that was typed. Anyone who wants
 * longer takes it simply by touching the panel.
 */
export const SITE_SETTLE_MS = 3000;

export class SitePanel {
  readonly root: HTMLElement;

  private readonly title: HTMLElement;
  private readonly zone: HTMLElement;
  /** The red "not localized yet" flag. See {@link renderHead}. */
  private readonly alert: HTMLElement;
  private readonly chevron: HTMLButtonElement;
  private readonly body: HTMLElement;
  private readonly section: LocalizeSection;
  private shut: boolean;
  /** Counting down to the auto-shut. Null whenever nothing is pending. See {@link SITE_SETTLE_MS}. */
  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly deps: SitePanelDeps) {
    this.section = new LocalizeSection({
      ...deps,
      // Every change to the profile redraws the head, so the title and the body can never disagree
      // about where the site is.
      onLocalize: (localization, options) => {
        deps.onLocalize(localization, options);
        this.refresh();
        // Only a profile arms the clock. Clearing the field is how you say "forget where this is",
        // and folding the panel away on top of that would hide the box you are about to retype in.
        if (localization === null) this.cancelSettle();
        else this.armSettle();
      },
      onConditions: (conditions) => {
        deps.onConditions(conditions);
        this.refresh();
      },
    });

    this.title = el('h2', { class: 'lg-panel__title' });
    this.zone = el('span', { class: 'lg-panel__count lg-site__zone' });
    this.alert = el(
      'span',
      {
        class: 'lg-site__alert',
        title: 'This site has no location, so no plant has been checked against its climate',
        attrs: { role: 'status' },
      },
      el('span', { class: 'lg-site__alerticon', html: icon('warning', 12) }),
      el('span', { text: 'Not localized yet' }),
    );
    this.chevron = el('button', {
      class: 'lg-panel__collapse',
      html: icon('chevron', 16),
      attrs: { type: 'button' },
      on: { click: () => this.toggle() },
    });

    this.body = el('div', { class: 'lg-panel__body' }, this.section.root);
    this.root = el(
      'aside',
      { class: 'lg-panel lg-panel--site', attrs: { 'aria-label': 'Site location' } },
      el(
        'div',
        { class: 'lg-panel__head' },
        el('span', { class: 'lg-panel__icon lg-site__pin', html: icon('pin', 16) }),
        this.title,
        this.alert,
        this.zone,
        this.chevron,
      ),
      this.body,
    );

    // Anywhere in the panel, capture phase: the chips and the buttons stop nothing, but a listener
    // on the bubble path would miss a click on a control that re-renders its own row out from under
    // the event — and "the user is working in here" is true either way.
    this.root.addEventListener('pointerdown', this.noteActivity, true);
    this.root.addEventListener('keydown', this.noteActivity, true);

    // Open when there is nothing to summarize, shut once there is.
    this.shut = deps.localization() !== null;
    this.refresh();
  }

  /** Redraw from the current profile — after localizing, and after a project is opened. */
  refresh(): void {
    const localization = this.deps.localization();
    this.renderHead(localization);
    this.section.refresh();
    this.applyShut();
    setClass(this.root, 'is-collapsed', this.deps.collapsed?.() ?? false);
  }

  /**
   * The head: a place and a zone, or a red flag saying there is neither.
   *
   * The alert is deliberately loud. Everything downstream of localization fails *quietly* without
   * it — the palette shows no badges, the ecology report dates itself against a reference climate
   * nobody chose, and every plant reads as merely "suitable" because nothing has actually been
   * checked. A user who does not know the site is unlocalized has no way to tell that apart from a
   * site where everything passed, and this is the one place that difference can be stated.
   */
  private renderHead(localization: SiteLocalization | null): void {
    const set = localization !== null;
    this.title.textContent = localization?.regionLabel ?? 'Localize site';
    this.zone.textContent = localization ? zoneLabel(localization.hardinessZone) : '';
    this.zone.hidden = !set;
    this.alert.hidden = set;
    this.title.title = set
      ? `${localization.regionLabel} · zone ${zoneLabel(localization.hardinessZone)}`
      : 'Say where this site is, and every plant is rated for its climate';
    setClass(this.root, 'is-set', set);
  }

  private toggle(): void {
    // The chevron is the user taking the decision back, in either direction: opened by hand it must
    // stay open, and shut by hand there is nothing left to wait for.
    this.cancelSettle();
    this.shut = !this.shut;
    this.applyShut();
  }

  /**
   * Restart the countdown if one is running.
   *
   * Deliberately only *if* — a click in a panel that is already settled must not raise a timer that
   * would then shut it while it is being used.
   */
  private readonly noteActivity = (): void => {
    if (this.settleTimer !== null) this.armSettle();
  };

  private armSettle(): void {
    this.cancelSettle();
    if (this.shut) return;
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      // Re-checked, because three seconds is long enough for the chevron, a project open, or a
      // cleared field to have made this the wrong thing to do.
      if (this.shut || this.deps.localization() === null) return;
      this.shut = true;
      this.applyShut();
      this.keepFocusInPanel();
    }, SITE_SETTLE_MS);
  }

  private cancelSettle(): void {
    if (this.settleTimer !== null) clearTimeout(this.settleTimer);
    this.settleTimer = null;
  }

  /**
   * Hand focus to the chevron when the body it was in has just been hidden.
   *
   * Localizing from the keyboard leaves focus on a control inside the body, and `display: none`
   * would drop it on the document — sending the next Tab back to the top of the page. The chevron is
   * where that focus belongs: it is the control that undoes what just happened.
   */
  private keepFocusInPanel(): void {
    const active = document.activeElement;
    if (active instanceof Node && this.body.contains(active)) this.chevron.focus();
  }

  private applyShut(): void {
    setClass(this.root, 'is-shut', this.shut);
    this.chevron.setAttribute('aria-expanded', String(!this.shut));
    this.chevron.title = this.shut ? 'Show site location' : 'Hide site location';
    this.chevron.setAttribute('aria-label', this.chevron.title);
  }

  dispose(): void {
    this.cancelSettle();
    this.root.removeEventListener('pointerdown', this.noteActivity, true);
    this.root.removeEventListener('keydown', this.noteActivity, true);
    this.root.remove();
  }
}
